import { useState, useEffect, useCallback, useRef } from "react";
import { API, axios } from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Coffee, Gift, QrCode, Sparkles, X, Check, Loader2, Camera, History } from "lucide-react";
import BrandCard from "@/components/shared/BrandCard";
import TieredPunchCard, { TIERS } from "@/components/punchcard/TieredPunchCard";
import TierLevelUpCelebration from "@/components/punchcard/TierLevelUpCelebration";
import CardArchive from "@/components/punchcard/CardArchive";

const CELEBRATION_DURATION = 7000;

export default function PunchCard({ businessId, businessName, onClose }) {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [celebrating, setCelebrating] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [pendingDwell, setPendingDwell] = useState(null);
  const [brandCardOpen, setBrandCardOpen] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [showTierCelebration, setShowTierCelebration] = useState(false);
  const [showTierProgress, setShowTierProgress] = useState(false);
  const celebrationTimer = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/punchcard/status/${businessId}`);
      setStatus(res.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [businessId]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleScan = async () => {
    setScanning(true);
    try {
      let lat = null, lng = null;
      try {
        const pos = await new Promise((res, rej) =>
          navigator.geolocation.getCurrentPosition(res, rej, { timeout: 5000 })
        );
        lat = pos.coords.latitude;
        lng = pos.coords.longitude;
      } catch { /* no GPS */ }

      const res = await axios.post(`${API}/punchcard/scan`, {
        business_id: businessId, lat, lng,
      });

      if (res.data.status === "confirmed") {
        const bonusMsg = res.data.bonus_punch ? " (2x Double Punch Day!)" : "";
        toast.success(`Punch ${res.data.punches}/${res.data.target} earned!${bonusMsg}`);
        if (res.data.card_complete) {
          setCelebrating(true);
          celebrationTimer.current = setTimeout(() => {
            setCelebrating(false);
            // After confetti: check tier advance prediction
            if (status?.tier?.enabled) {
              const cardsAtTier = status.tier.cards_completed_at_current_tier || 0;
              const currentTier = status.tier.current_tier || 1;
              if (cardsAtTier + 1 >= 2 && currentTier < 9) {
                setShowTierCelebration(true);
              } else {
                setShowTierProgress(true);
              }
            }
          }, CELEBRATION_DURATION);
        }
      } else if (res.data.status === "pending") {
        if (res.data.mode === "tap_to_confirm") {
          toast.success("Punch submitted! Waiting for vendor to confirm.");
        } else if (res.data.mode === "return_to_zone") {
          toast.success("Punch started! Come back to pick up your order to confirm.");
          setPendingDwell(res.data.punch_id);
        }
      }
      fetchStatus();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not scan punch";
      toast.error(msg);
    } finally { setScanning(false); }
  };

  const handleDwellConfirm = async () => {
    if (!pendingDwell) return;
    try {
      let lat = null, lng = null;
      try {
        const pos = await new Promise((res, rej) =>
          navigator.geolocation.getCurrentPosition(res, rej, { timeout: 5000 })
        );
        lat = pos.coords.latitude;
        lng = pos.coords.longitude;
      } catch { /* no GPS */ }

      const res = await axios.post(`${API}/punchcard/dwell-confirm`, {
        punch_id: pendingDwell, lat, lng,
      });
      toast.success("Punch confirmed!");
      setPendingDwell(null);
      if (res.data.card_complete) {
        setCelebrating(true);
        celebrationTimer.current = setTimeout(() => {
          setCelebrating(false);
          if (status?.tier?.enabled) {
            const cardsAtTier = status.tier.cards_completed_at_current_tier || 0;
            const currentTier = status.tier.current_tier || 1;
            if (cardsAtTier + 1 >= 2 && currentTier < 9) {
              setShowTierCelebration(true);
            } else {
              setShowTierProgress(true);
            }
          }
        }, CELEBRATION_DURATION);
      }
      fetchStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not confirm punch");
    }
  };

  const handleRedeem = async () => {
    setRedeeming(true);
    try {
      const res = await axios.post(`${API}/punchcard/redeem`, {
        business_id: businessId,
      });
      toast.success("Reward requested! Show this to the vendor.");
      fetchStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not redeem");
    } finally { setRedeeming(false); }
  };

  useEffect(() => {
    return () => { if (celebrationTimer.current) clearTimeout(celebrationTimer.current); };
  }, []);

  // Tier level up celebration overlay (on mount or after confetti prediction)
  if (showTierCelebration) {
    return (
      <TierLevelUpCelebration
        businessId={businessId}
        onDone={() => { setShowTierCelebration(false); fetchStatus(); }}
      />
    );
  }

  // Tier progress screen (card X of 2, no level up this time)
  if (showTierProgress && status?.tier?.enabled) {
    const t = TIERS[status.tier.current_tier] || TIERS[1];
    const cardsAtTier = (status.tier.cards_completed_at_current_tier || 0) + 1; // includes this completion
    const isReady = cardsAtTier >= 2;
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 px-6" data-testid="tier-progress-screen">
        <div className="w-full max-w-sm text-center space-y-5">
          <div className="w-48 mx-auto">
            <TieredPunchCard tier={status.tier.current_tier} punches_earned={status.target} punches_required={status.target} business_name={businessName} reward={status.reward_description || ""} />
          </div>
          <p className="text-base font-bold" style={{ color: t.accent }}>
            {isReady
              ? `Ready to level up on next card!`
              : `Card ${cardsAtTier} of 2 complete at ${t.name} level \u2014 ${2 - cardsAtTier} more to level up`}
          </p>
          <button
            onClick={() => { setShowTierProgress(false); }}
            className="w-full py-4 rounded-xl bg-[#F59E0B] text-[#1C1C1E] font-black text-base active:scale-[0.98] transition"
            data-testid="tier-progress-done-btn"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  // Celebration overlay
  if (celebrating) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-gradient-to-br from-amber-500 via-[#F59E0B] to-orange-500" data-testid="punch-celebration">
        <div className="text-center px-8 animate-[scaleIn_0.5s_ease-out]">
          <div className="text-8xl mb-4" onClick={() => setBrandCardOpen(true)} data-testid="brand-tap-punch">🎉</div>
          <h1 className="text-3xl font-black text-white mb-2">CARD COMPLETE!</h1>
          <p className="text-lg text-white/80 font-medium mb-1">
            {status?.reward_description || "Your reward is ready!"}
          </p>
          <p className="text-sm text-white/60">Show this to the vendor to claim</p>
          <div className="mt-6 flex gap-3 justify-center">
            <button
              onClick={() => { setCelebrating(false); handleRedeem(); }}
              className="px-6 py-3 rounded-full bg-white text-amber-600 font-bold text-sm shadow-lg active:scale-95 transition"
              data-testid="celebration-redeem-btn"
            >
              <Gift className="w-4 h-4 inline mr-1.5" /> Redeem Now
            </button>
            <button
              onClick={() => setCelebrating(false)}
              className="px-6 py-3 rounded-full bg-white/20 text-white font-bold text-sm active:scale-95 transition"
            >
              Later
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-slate-50 rounded-xl p-4 flex items-center justify-center">
        <Loader2 className="w-5 h-5 text-[#F59E0B] animate-spin" />
      </div>
    );
  }

  if (!status) return null;

  const { punches, pending, target, card_complete, reward_description, confirmation_mode, tier } = status;
  const isTiered = tier?.enabled;

  // Archive view
  if (showArchive) {
    return <CardArchive businessId={businessId} businessName={businessName} onClose={() => setShowArchive(false)} />;
  }

  return (
    <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-100" data-testid="punch-card">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-[#F59E0B] flex items-center justify-center">
            <Coffee className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="text-sm font-bold text-slate-800">
              {isTiered ? `${tier.tier_name} Loyalty Card` : "Loyalty Card"}
            </p>
            <p className="text-[10px] text-slate-500">{punches}/{target} punches</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {status.double_punch_day && (
            <span className="text-[10px] font-bold text-orange-600 bg-orange-100 px-2 py-0.5 rounded-full flex items-center gap-0.5" data-testid="double-punch-badge">
              <Sparkles className="w-3 h-3" /> 2x Today
            </span>
          )}
          {status.redemptions > 0 && (
            <span className="text-[10px] text-amber-600 font-medium bg-amber-100 px-2 py-0.5 rounded-full">
              {status.redemptions}x redeemed
            </span>
          )}
        </div>
      </div>

      {/* Tiered SVG Card or default Stamp Grid */}
      {isTiered ? (
        <div className="mb-3">
          <TieredPunchCard
            tier={tier.current_tier}
            punches_earned={punches}
            punches_required={target}
            business_name={businessName}
            reward={reward_description || ""}
          />
        </div>
      ) : (
        <div className="grid grid-cols-5 gap-2 mb-3" data-testid="punch-stamp-grid">
          {Array.from({ length: target }).map((_, i) => {
            const isEarned = i < punches;
            const isPending = i >= punches && i < punches + pending;
            return (
              <div key={i} className={`aspect-square rounded-lg flex items-center justify-center transition-all ${isEarned ? "bg-[#F59E0B] shadow-sm" : isPending ? "bg-amber-200 border-2 border-dashed border-amber-400" : "bg-white border-2 border-slate-200"}`} data-testid={`punch-stamp-${i}`}>
                {isEarned ? <Coffee className="w-4 h-4 text-white" /> : isPending ? <Loader2 className="w-3 h-3 text-amber-500 animate-spin" /> : i === target - 1 ? <Gift className="w-4 h-4 text-slate-300" /> : <span className="text-[10px] text-slate-300 font-bold">{i + 1}</span>}
              </div>
            );
          })}
        </div>
      )}

      {/* Reward description */}
      <p className="text-[11px] text-amber-700 font-medium text-center mb-3">
        {card_complete ? "Card complete! Redeem your reward below." : `${target - punches} more to go — ${reward_description}`}
      </p>

      {/* Pending dwell confirmation */}
      {pendingDwell && (
        <button
          onClick={handleDwellConfirm}
          className="w-full mb-2 py-2.5 rounded-lg bg-blue-500 text-white font-bold text-sm flex items-center justify-center gap-1.5 active:scale-[0.98] transition"
          data-testid="dwell-confirm-btn"
        >
          <Check className="w-4 h-4" /> I'm back — confirm my punch
        </button>
      )}

      {/* Actions */}
      {card_complete ? (
        <button
          onClick={handleRedeem}
          disabled={redeeming}
          className="w-full py-2.5 rounded-lg bg-gradient-to-r from-[#F59E0B] to-orange-500 text-white font-bold text-sm flex items-center justify-center gap-1.5 shadow-md active:scale-[0.98] transition disabled:opacity-50"
          data-testid="redeem-reward-btn"
        >
          {redeeming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />}
          Redeem Reward
        </button>
      ) : (
        <button
          onClick={handleScan}
          disabled={scanning}
          className="w-full py-2.5 rounded-lg bg-[#F59E0B] text-[#1C1C1E] font-bold text-sm flex items-center justify-center gap-1.5 active:scale-[0.98] transition disabled:opacity-50"
          data-testid="scan-punch-btn"
        >
          {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <QrCode className="w-4 h-4" />}
          Scan to Earn Punch
        </button>
      )}

      {/* Mode indicator */}
      <p className="text-[9px] text-center text-slate-400 mt-2">
        {confirmation_mode === "trust_based" && "Auto-confirmed near the truck"}
        {confirmation_mode === "return_to_zone" && "Confirmed when you return to pick up"}
        {confirmation_mode === "tap_to_confirm" && "Vendor confirms your punch"}
      </p>

      {/* Archive history link */}
      {isTiered && (
        <button
          onClick={() => setShowArchive(true)}
          className="w-full mt-2 text-[10px] text-slate-400 hover:text-amber-600 flex items-center justify-center gap-1 transition"
          data-testid="card-history-link"
        >
          <History className="w-3 h-3" /> History
        </button>
      )}

      <BrandCard
        isOpen={brandCardOpen}
        onClose={() => setBrandCardOpen(false)}
        businessId={businessId}
        businessName={businessName}
      />
    </div>
  );
}
